import { Filter } from '~/components/table/header/filters/useFilters';
import { TransactionsViewOption } from '~/sections/agents/profile/types';

export const QueryKeys = {
  currentUser: () => 'currentUser',

  uiSettings: (id: number) => ['uiSettings', id],

  verificationOptions: () => 'verificationOptions',

  collaborators: (sortByFilters: string) => ['collaborators', sortByFilters],

  cities: (city: string, county: string) => ['cities', city, county],

  counties: (county: string, state: string) => ['counties', county, state],

  agent: (id: string, period?: Period) => ['agent', id, period],

  agentPerformance: (id: string, period?: Period) => ['agentPerformance', id, period],

  agents: () => 'agents',

  teams: () => 'teams',

  agentTeamMembers: (id: string) => ['teamMembers', id],

  loanOfficer: (nmls: string, period?: Period) => ['loanOfficer', nmls, period],

  loanOfficers: () => 'loanOfficers',

  companyEmployees: (license: string, period: Period) => ['employees', license, period],

  companyOffices: (license: string, period: Period) => ['offices', license, period],

  company: (license: string, period: string, trend_period: string) => [
    'company',
    license,
    period,
    trend_period,
  ],

  companyTransactions: (license: string, period: Period) => [
    'companyTransactions',
    license,
    period,
  ],

  loanOfficersTransactions: (filterDataPeriod: string, only_transactions: boolean) => [
    'loanOfficersTransactions',
    filterDataPeriod,
    only_transactions,
  ],

  loanOfficersTransactionsOnly: (filterDataPeriod: string, only_transactions: boolean) => [
    'loanOfficersTransactionsOnly',
    filterDataPeriod,
    only_transactions,
  ],

  dashboardLoanOfficers: (
    period: number,
    value: string[],
    page: number,
    limit: number,
    criteria?: string,
    block?: string
  ) => ['dashboardLoanOfficers', period, criteria, page, limit, block, value],

  dashboardCompanies: (
    period: number,
    value: string[],
    page: number,
    limit: number,
    criteria?: string,
    block?: string
  ) => ['dashboardCompanies', period, value, page, limit, criteria, block],

  dashboardLoLoansType: (
    period: number,
    criteria: string,
    page: number,
    limit: number,
    block?: string
  ) => ['dashboardLoLoansType', period, criteria, page, limit, block],

  dashboardLoTransactionsType: (
    period: number,
    criteria: string,
    page: number,
    limit: number,
    block?: string
  ) => ['dashboardLoTransactionsType', period, criteria, page, limit, block],

  dashboardLoTransactionsChart: (period: number, block?: string) => [
    'dashboardLoTransactionsChart',
    period,
    block,
  ],

  dashboardLoLoansTypeChart: (period: number, block?: string) => [
    'dashboardLoLoansTypeChart',
    period,
    block,
  ],

  agentCompaniesBuyerSide: (
    nmls: string,
    page: number,
    period: Period,
    sort_field: string,
    order: string
  ) => ['agentCompaniesBuyerSide', nmls, page, period, sort_field, order],

  agentCompaniesSellerSide: (
    nmls: string,
    page: number,
    period: Period,
    sort_field: string,
    order: string
  ) => ['agentCompaniesSellerSide', nmls, page, period, sort_field, order],

  agentCompany: (nmls: string, company_license: string, company_type: string, period: Period) => [
    'agentCompany',
    nmls,
    company_license,
    company_type,
    period,
  ],

  agentsBuyerSide: (
    nmls: string,
    page: number,
    period: Period,
    sort_field: string,
    order: string
  ) => ['agentsBuyerSide', nmls, page, period, sort_field, order],

  agentsSellerSide: (
    nmls: string,
    page: number,
    period: Period,
    sort_field: string,
    order: string
  ) => ['agentsSellerSide', nmls, page, period, sort_field, order],

  companyAgentsBuyerSide: (
    license: string,
    period: Period,
    limit: number,
    page: number,
    sort_field: string,
    order: string
  ) => ['companyAgentsBuyerSide', license, period, limit, page, sort_field, order],

  companyAgentsSellerSide: (
    license: string,
    period: Period,
    limit: number,
    page: number,
    sort_field: string,
    order: string
  ) => ['companyAgentsSellerSide', license, period, limit, page, sort_field, order],

  companyAgentsStatsSellerSide: (license: string, period: Period) => [
    'companyAgentsStatsSellerSide',
    license,
    period,
  ],

  companyAgentsStatsBuyerSide: (license: string, period: Period) => [
    'companyAgentsStatsBuyerSide',
    license,
    period,
  ],

  companyAgentsCompaniesBuyerSide: (
    license: string,
    period: Period,
    limit: number,
    page: number,
    sort_field: string,
    order: string
  ) => ['companyAgentsCompaniesBuyerSide', license, period, page, limit, sort_field, order],

  companyAgentsCompaniesSellerSide: (
    license: string,
    period: Period,
    limit: number,
    page: number,
    sort_field: string,
    order: string
  ) => ['companyAgentsCompaniesSellerSide', license, period, page, limit, sort_field, order],

  companyAgentsCompaniesStatsSellerSide: (license: string, period: Period) => [
    'companyAgentsCompaniesStatsSellerSide',
    license,
    period,
  ],

  companyAgentsCompaniesStatsBuyerSide: (license: string, period: Period) => [
    'companyAgentsCompaniesStatsBuyerSide',
    license,
    period,
  ],

  agentCompanyConnection: (
    license: string,
    company_name: string,
    company_type: string,
    period: Period
  ) => ['agentCompany', license, company_name, company_type, period],

  agentLoanOfficers: (
    id: string,
    page: number,
    sort_field: string,
    order: string,
    period: Period,
    view: AgentTeamScopeOption
  ) => ['agentLoanOfficers', id, page, sort_field, order, period, view],

  agentLoanOfficerCompanies: (
    id: string,
    page: number,
    sort_field: string,
    order: string,
    period: Period,
    view: AgentTeamScopeOption
  ) => ['agentLoanOfficerCompanies', id, page, sort_field, order, period, view],

  agentLoanOfficerCompany: (id: string, company_name: string, period: Period) => [
    'agentLoanOfficerCompany',
    id,
    company_name,
    period,
  ],

  list: (id: number) => ['lists', id],
  lists: () => 'lists',
  companies: (name: string) => ['companies', name],

  loan_types: () => 'loanTypes',

  transactions: (
    transactionTypesFilters: Filter[],
    loanTypesFilters: Filter[],
    agenciesFilters: Filter[],
    agentRolesFilters?: Filter[],
    view?: TransactionsViewOption
  ) => [
    'transactions',
    transactionTypesFilters,
    loanTypesFilters,
    agenciesFilters,
    agentRolesFilters,
    view,
  ],

  team: (id: number, period: Period) => ['teams', id, period],

  companyTrends: () => 'companyTrends',
  companyNewTrend: () => 'companyNewTrend',
  companyPriorTrend: () => 'companyPriorTrend',
  opportunities: () => 'opportunities',
};
